import { type Language } from 'ecosystem'

/**
 * All user-facing strings for the invoice-mode UI. Consumed by
 * `InvoiceReviewPanel` (the review + submit view on the payment stage) and
 * `InvoiceConfirmation` (the post-submit success page). The `orderMode*` keys
 * feed `OrderModeSelect` — the card/invoice switcher rendered on the checkout
 * summary for companies whose sales terms allow invoice billing
 * (`companyUsesInvoiceFlow`); companies on UPFRONT terms never see it.
 *
 * Defaults live here per locale; brand-level overrides supplied via the
 * `CheckoutPageStd.config.overrides` map still win because they're merged in
 * by an outer `OverridesContextProvider`.
 */
export interface InvoicePaymentOverrides {
  // OrderModeSelect
  orderModeUpfrontLabel: string
  orderModeUpfrontDescription: string
  orderModeInvoiceLabel: string
  orderModeInvoiceDescription: string
  orderModeInvoiceDescriptionProforma: string

  // CheckoutSummary (invoice-related)
  checkoutSummaryOrderModeHeading: string
  checkoutSummarySummaryContinueToReview: string

  // InvoiceReviewPanel
  invoiceReviewHeading: string
  invoiceReviewBackToCart: string
  invoiceReviewOrderPrefix: string
  invoiceReviewOrderHelp: string
  invoiceReviewOrderHelpProforma: string
  invoiceReviewItemsHeading: string
  invoiceReviewEmptyItems: string
  invoiceReviewNetTotal: string
  invoiceReviewShipping: string
  invoiceReviewShippingInclVat: string
  invoiceReviewShippingExclVat: string
  invoiceReviewVat: string
  invoiceReviewVatNoteReverseCharge: string
  invoiceReviewVatNoteExport: string
  invoiceReviewDiscount: string
  invoiceReviewQuantityDiscount: string
  invoiceReviewTotal: string
  invoiceReviewTotalProforma: string
  invoiceReviewSubmitLabel: string
  invoiceReviewLoadError: string

  // InvoiceConfirmation
  invoiceConfirmationTitle: string
  invoiceConfirmationOrderNumberPrefix: string
  invoiceConfirmationBody: string
  invoiceConfirmationBodyProforma: string

  // InvoiceBillingAddress (folded card + picker)
  invoiceBillingHeading: string
  invoiceBillingFoldedHint: string
  invoiceBillingChange: string
  invoiceBillingCancel: string
  invoiceBillingSaveCustom: string
  invoiceBillingCustomOption: string
  invoiceBillingEmpty: string
  invoiceBillingFieldAttention: string
  invoiceBillingFieldAddress1: string
  invoiceBillingFieldAddress2: string
  invoiceBillingFieldPostalCode: string
  invoiceBillingFieldPostalRegion: string
  invoiceBillingFieldCountry: string
  invoiceBillingFieldMobilePhone: string
  invoiceBillingFieldReference: string
  invoiceBillingRememberOnDevice: string

  // InvoiceShippingAddress (folded card + picker) — field labels reuse the
  // invoiceBillingField* keys above since OrderAddressDTO is the same shape.
  invoiceShippingHeading: string
  invoiceShippingFoldedHint: string
  invoiceShippingChange: string
  invoiceShippingCancel: string
  invoiceShippingSaveCustom: string
  invoiceShippingCustomOption: string
  invoiceShippingEmpty: string

  // InvoiceDeliveryMethodSelector + pickup heading
  invoiceDeliveryLabel: string
  invoiceDeliveryDescription: string
  invoiceDeliveryFreeLabel: string
  invoicePickupLabel: string
  invoicePickupDescription: string
  invoicePickupHeading: string

  // VatNumberNotice — shown whenever the company's VAT number changes what the
  // buyer pays, or would break the order downstream.
  vatNumberNoticeMissing: string
  vatNumberNoticeInvalid: string
  vatNumberNoticeReverseChargeUnverifiable: string
}

export const invoicePaymentTranslations: Record<Language, InvoicePaymentOverrides> = {
  sv: {
    orderModeUpfrontLabel: 'Betala direkt',
    orderModeUpfrontDescription: 'Betala din beställning direkt i kassan.',
    orderModeInvoiceLabel: 'Faktura',
    orderModeInvoiceDescription: 'Faktureras till företaget efter beställning.',
    orderModeInvoiceDescriptionProforma: 'Proformafaktura – betalning krävs innan leverans.',

    checkoutSummaryOrderModeHeading: 'Betalningssätt',
    checkoutSummarySummaryContinueToReview: 'Granska faktura',

    invoiceReviewHeading: 'Granska faktura',
    invoiceReviewBackToCart: 'Tillbaka till varukorgen',
    invoiceReviewOrderPrefix: 'Order #',
    invoiceReviewOrderHelp:
      'Faktura skickas till företaget efter att beställningen är registrerad.',
    invoiceReviewOrderHelpProforma:
      'En proformafaktura skickas till företaget. Beställningen levereras efter att betalningen har mottagits.',
    invoiceReviewItemsHeading: 'Artiklar',
    invoiceReviewEmptyItems: 'Inga artiklar',
    invoiceReviewNetTotal: 'Summa exkl. moms',
    invoiceReviewShipping: 'Frakt',
    invoiceReviewShippingInclVat: 'inkl. moms',
    invoiceReviewShippingExclVat: 'exkl. moms',
    invoiceReviewVat: 'Moms',
    invoiceReviewVatNoteReverseCharge: 'VAT 0 % omvänd skattskyldighet (unionsintern försäljning)',
    invoiceReviewVatNoteExport: 'VAT 0 % export utanför EU',
    invoiceReviewDiscount: 'Rabatt',
    invoiceReviewQuantityDiscount: 'Mängdrabatt',
    invoiceReviewTotal: 'Totalt att fakturera',
    invoiceReviewTotalProforma: 'Totalt att förskottsbetala',
    invoiceReviewSubmitLabel: 'Skicka beställning',
    invoiceReviewLoadError: 'Beställning kunde inte laddas. Försök igen.',

    invoiceConfirmationTitle: 'Tack för din beställning!',
    invoiceConfirmationOrderNumberPrefix: 'Ordernummer: ',
    invoiceConfirmationBody:
      'En faktura skickas till företaget enligt avtalade villkor. Du kan följa orderns status under Mina beställningar.',
    invoiceConfirmationBodyProforma:
      'En proformafaktura skickas till företaget. Beställningen behandlas när betalningen har mottagits. Du kan följa orderns status under Mina beställningar.',

    invoiceBillingHeading: 'Faktureringsadress',
    invoiceBillingFoldedHint: 'Vald adress',
    invoiceBillingChange: 'Ändra',
    invoiceBillingCancel: 'Avbryt',
    invoiceBillingSaveCustom: 'Spara adress',
    invoiceBillingCustomOption: 'Anpassad adress',
    invoiceBillingEmpty: 'Ingen faktureringsadress vald.',
    invoiceBillingFieldAttention: 'Att / Kontaktperson',
    invoiceBillingFieldAddress1: 'Adress',
    invoiceBillingFieldAddress2: 'Adress rad 2',
    invoiceBillingFieldPostalCode: 'Postnummer',
    invoiceBillingFieldPostalRegion: 'Ort',
    invoiceBillingFieldCountry: 'Land',
    invoiceBillingFieldMobilePhone: 'Telefon',
    invoiceBillingFieldReference: 'Referens',
    invoiceBillingRememberOnDevice: 'Kom ihåg mina uppgifter på den här enheten',

    invoiceShippingHeading: 'Leveransadress',
    invoiceShippingFoldedHint: 'Vald adress',
    invoiceShippingChange: 'Ändra',
    invoiceShippingCancel: 'Avbryt',
    invoiceShippingSaveCustom: 'Spara adress',
    invoiceShippingCustomOption: 'Anpassad adress',
    invoiceShippingEmpty: 'Ingen leveransadress vald.',

    invoiceDeliveryLabel: 'Leverans',
    invoiceDeliveryDescription: 'Vi levererar till önskad adress',
    invoiceDeliveryFreeLabel: 'Fri frakt',
    invoicePickupLabel: 'Hämta i butik',
    invoicePickupDescription: 'Hämta i vår butik (frakt tas bort)',
    invoicePickupHeading: 'Välj upphämtningsställe',

    vatNumberNoticeMissing:
      'Företaget har inget momsregistreringsnummer registrerat, därför debiteras moms enligt ditt lands skattesats. Lägg till ett giltigt VAT-nummer i företagsprofilen för att handla utan moms.',
    vatNumberNoticeInvalid:
      'Momsregistreringsnumret ser inte giltigt ut – det ska inledas med landskoden, t.ex. PL9191127821. Tills det är korrigerat debiteras moms enligt ditt lands skattesats.',
    vatNumberNoticeReverseChargeUnverifiable:
      'Företaget är inställt på omvänd skattskyldighet, men momsregistreringsnumret saknas eller är felaktigt. Kontrollera numret innan du beställer – annars kan ordern inte bokföras.'
  },
  en: {
    orderModeUpfrontLabel: 'Pay now',
    orderModeUpfrontDescription: 'Pay for your order directly at checkout.',
    orderModeInvoiceLabel: 'Invoice',
    orderModeInvoiceDescription: 'Invoiced to the company after the order is placed.',
    orderModeInvoiceDescriptionProforma: 'Pro-forma invoice – payment required before delivery.',

    checkoutSummaryOrderModeHeading: 'Payment method',
    checkoutSummarySummaryContinueToReview: 'Review invoice',

    invoiceReviewHeading: 'Review invoice',
    invoiceReviewBackToCart: 'Back to cart',
    invoiceReviewOrderPrefix: 'Order #',
    invoiceReviewOrderHelp: 'An invoice will be sent to the company once the order is registered.',
    invoiceReviewOrderHelpProforma:
      'A pro-forma invoice will be sent to the company. The order ships once payment has been received.',
    invoiceReviewItemsHeading: 'Items',
    invoiceReviewEmptyItems: 'No items',
    invoiceReviewNetTotal: 'Subtotal excl. VAT',
    invoiceReviewShipping: 'Shipping',
    invoiceReviewShippingInclVat: 'incl. VAT',
    invoiceReviewShippingExclVat: 'excl. VAT',
    invoiceReviewVat: 'VAT',
    invoiceReviewVatNoteReverseCharge: 'VAT 0 % Intra-Community supply',
    invoiceReviewVatNoteExport: 'VAT 0 % Export outside the EU',
    invoiceReviewDiscount: 'Discount',
    invoiceReviewQuantityDiscount: 'Quantity discount',
    invoiceReviewTotal: 'Total to invoice',
    invoiceReviewTotalProforma: 'Total to prepay',
    invoiceReviewSubmitLabel: 'Submit order',
    invoiceReviewLoadError: 'Order could not be loaded. Please try again.',

    invoiceConfirmationTitle: 'Thank you for your order!',
    invoiceConfirmationOrderNumberPrefix: 'Order number: ',
    invoiceConfirmationBody:
      'An invoice will be sent to the company according to the agreed terms. You can track the order status under My orders.',
    invoiceConfirmationBodyProforma:
      'A pro-forma invoice will be sent to the company. The order is processed once payment has been received. You can track the order status under My orders.',

    invoiceBillingHeading: 'Billing address',
    invoiceBillingFoldedHint: 'Selected address',
    invoiceBillingChange: 'Change',
    invoiceBillingCancel: 'Cancel',
    invoiceBillingSaveCustom: 'Save address',
    invoiceBillingCustomOption: 'Custom address',
    invoiceBillingEmpty: 'No billing address selected.',
    invoiceBillingFieldAttention: 'Attn / Contact person',
    invoiceBillingFieldAddress1: 'Address',
    invoiceBillingFieldAddress2: 'Address line 2',
    invoiceBillingFieldPostalCode: 'Postal code',
    invoiceBillingFieldPostalRegion: 'City',
    invoiceBillingFieldCountry: 'Country',
    invoiceBillingFieldMobilePhone: 'Phone',
    invoiceBillingFieldReference: 'Reference',
    invoiceBillingRememberOnDevice: 'Remember my details on this device',

    invoiceShippingHeading: 'Shipping address',
    invoiceShippingFoldedHint: 'Selected address',
    invoiceShippingChange: 'Change',
    invoiceShippingCancel: 'Cancel',
    invoiceShippingSaveCustom: 'Save address',
    invoiceShippingCustomOption: 'Custom address',
    invoiceShippingEmpty: 'No shipping address selected.',

    invoiceDeliveryLabel: 'Delivery',
    invoiceDeliveryDescription: 'We deliver to address of your choice',
    invoiceDeliveryFreeLabel: 'Free shipping',
    invoicePickupLabel: 'Pick up in store',
    invoicePickupDescription: 'You pick up in our store (shipping is cancelled)',
    invoicePickupHeading: 'Select pickup location',

    vatNumberNoticeMissing:
      'No VAT registration number is recorded for this company, so VAT is charged at your country\u2019s rate. Add a valid VAT number to the company profile to buy without VAT.',
    vatNumberNoticeInvalid:
      'The VAT registration number does not look valid \u2014 it should start with the country code, e.g. PL9191127821. Until it is corrected, VAT is charged at your country\u2019s rate.',
    vatNumberNoticeReverseChargeUnverifiable:
      'This company is set to reverse charge, but its VAT registration number is missing or malformed. Check the number before ordering \u2014 otherwise the order cannot be booked.'
  },
  no: {
    orderModeUpfrontLabel: 'Betal nå',
    orderModeUpfrontDescription: 'Betal bestillingen din direkte i kassen.',
    orderModeInvoiceLabel: 'Faktura',
    orderModeInvoiceDescription: 'Faktureres til selskapet etter bestilling.',
    orderModeInvoiceDescriptionProforma: 'Proformafaktura – betaling kreves før levering.',

    checkoutSummaryOrderModeHeading: 'Betalingsmåte',
    checkoutSummarySummaryContinueToReview: 'Gjennomgå faktura',

    invoiceReviewHeading: 'Gjennomgå faktura',
    invoiceReviewBackToCart: 'Tilbake til handlekurv',
    invoiceReviewOrderPrefix: 'Ordre #',
    invoiceReviewOrderHelp: 'En faktura sendes til selskapet når bestillingen er registrert.',
    invoiceReviewOrderHelpProforma:
      'En proformafaktura sendes til selskapet. Bestillingen sendes når betalingen er mottatt.',
    invoiceReviewItemsHeading: 'Varer',
    invoiceReviewEmptyItems: 'Ingen varer',
    invoiceReviewNetTotal: 'Sum eks. mva',
    invoiceReviewShipping: 'Frakt',
    invoiceReviewShippingInclVat: 'inkl. mva',
    invoiceReviewShippingExclVat: 'ekskl. mva',
    invoiceReviewVat: 'Mva',
    invoiceReviewVatNoteReverseCharge: 'VAT 0 % omvendt avgiftsplikt (EU-handel)',
    invoiceReviewVatNoteExport: 'VAT 0 % eksport utenfor EU',
    invoiceReviewDiscount: 'Rabatt',
    invoiceReviewQuantityDiscount: 'Mengderabatt',
    invoiceReviewTotal: 'Totalt å fakturere',
    invoiceReviewTotalProforma: 'Totalt å forhåndsbetale',
    invoiceReviewSubmitLabel: 'Send bestilling',
    invoiceReviewLoadError: 'Bestillingen kunne ikke lastes. Prøv igjen.',

    invoiceConfirmationTitle: 'Takk for din bestilling!',
    invoiceConfirmationOrderNumberPrefix: 'Ordrenummer: ',
    invoiceConfirmationBody:
      'En faktura sendes til selskapet i henhold til avtalte vilkår. Du kan følge ordrens status under Mine bestillinger.',
    invoiceConfirmationBodyProforma:
      'En proformafaktura sendes til selskapet. Bestillingen behandles når betalingen er mottatt. Du kan følge ordrens status under Mine bestillinger.',

    invoiceBillingHeading: 'Faktureringsadresse',
    invoiceBillingFoldedHint: 'Valgt adresse',
    invoiceBillingChange: 'Endre',
    invoiceBillingCancel: 'Avbryt',
    invoiceBillingSaveCustom: 'Lagre adresse',
    invoiceBillingCustomOption: 'Egendefinert adresse',
    invoiceBillingEmpty: 'Ingen faktureringsadresse valgt.',
    invoiceBillingFieldAttention: 'Att / Kontaktperson',
    invoiceBillingFieldAddress1: 'Adresse',
    invoiceBillingFieldAddress2: 'Adresse linje 2',
    invoiceBillingFieldPostalCode: 'Postnummer',
    invoiceBillingFieldPostalRegion: 'Sted',
    invoiceBillingFieldCountry: 'Land',
    invoiceBillingFieldMobilePhone: 'Telefon',
    invoiceBillingFieldReference: 'Referanse',
    invoiceBillingRememberOnDevice: 'Husk opplysningene mine på denne enheten',

    invoiceShippingHeading: 'Leveringsadresse',
    invoiceShippingFoldedHint: 'Valgt adresse',
    invoiceShippingChange: 'Endre',
    invoiceShippingCancel: 'Avbryt',
    invoiceShippingSaveCustom: 'Lagre adresse',
    invoiceShippingCustomOption: 'Egendefinert adresse',
    invoiceShippingEmpty: 'Ingen leveringsadresse valgt.',

    invoiceDeliveryLabel: 'Levering',
    invoiceDeliveryDescription: 'Vi leverer til ønsket adresse',
    invoiceDeliveryFreeLabel: 'Gratis frakt',
    invoicePickupLabel: 'Hent i butikk',
    invoicePickupDescription: 'Hent i butikken vår (frakt fjernes)',
    invoicePickupHeading: 'Velg hentested',

    vatNumberNoticeMissing:
      'Selskapet har ikke registrert et MVA-nummer, derfor beregnes mva etter ditt lands sats. Legg til et gyldig VAT-nummer i firmaprofilen for å handle uten mva.',
    vatNumberNoticeInvalid:
      'MVA-nummeret ser ikke gyldig ut – det skal begynne med landkoden, f.eks. PL9191127821. Inntil det er rettet beregnes mva etter ditt lands sats.',
    vatNumberNoticeReverseChargeUnverifiable:
      'Selskapet er satt til omvendt avgiftsplikt, men MVA-nummeret mangler eller er feil. Kontroller nummeret før du bestiller – ellers kan ikke ordren bokføres.'
  },
  da: {
    orderModeUpfrontLabel: 'Betal nu',
    orderModeUpfrontDescription: 'Betal din bestilling direkte i kassen.',
    orderModeInvoiceLabel: 'Faktura',
    orderModeInvoiceDescription: 'Faktureres til virksomheden efter bestilling.',
    orderModeInvoiceDescriptionProforma: 'Proformafaktura – betaling kræves inden levering.',

    checkoutSummaryOrderModeHeading: 'Betalingsmetode',
    checkoutSummarySummaryContinueToReview: 'Gennemgå faktura',

    invoiceReviewHeading: 'Gennemgå faktura',
    invoiceReviewBackToCart: 'Tilbage til kurven',
    invoiceReviewOrderPrefix: 'Ordre #',
    invoiceReviewOrderHelp: 'En faktura sendes til virksomheden, når bestillingen er registreret.',
    invoiceReviewOrderHelpProforma:
      'En proformafaktura sendes til virksomheden. Bestillingen sendes, når betalingen er modtaget.',
    invoiceReviewItemsHeading: 'Varer',
    invoiceReviewEmptyItems: 'Ingen varer',
    invoiceReviewNetTotal: 'Subtotal ekskl. moms',
    invoiceReviewShipping: 'Fragt',
    invoiceReviewShippingInclVat: 'inkl. moms',
    invoiceReviewShippingExclVat: 'ekskl. moms',
    invoiceReviewVat: 'Moms',
    invoiceReviewVatNoteReverseCharge: 'VAT 0 % omvendt betalingspligt (EU-handel)',
    invoiceReviewVatNoteExport: 'VAT 0 % eksport uden for EU',
    invoiceReviewDiscount: 'Rabat',
    invoiceReviewQuantityDiscount: 'Mængderabat',
    invoiceReviewTotal: 'Total til fakturering',
    invoiceReviewTotalProforma: 'Total til forudbetaling',
    invoiceReviewSubmitLabel: 'Send bestilling',
    invoiceReviewLoadError: 'Bestillingen kunne ikke indlæses. Prøv igen.',

    invoiceConfirmationTitle: 'Tak for din bestilling!',
    invoiceConfirmationOrderNumberPrefix: 'Ordrenummer: ',
    invoiceConfirmationBody:
      'En faktura sendes til virksomheden i henhold til de aftalte vilkår. Du kan følge ordrens status under Mine bestillinger.',
    invoiceConfirmationBodyProforma:
      'En proformafaktura sendes til virksomheden. Bestillingen behandles, når betalingen er modtaget. Du kan følge ordrens status under Mine bestillinger.',

    invoiceBillingHeading: 'Faktureringsadresse',
    invoiceBillingFoldedHint: 'Valgt adresse',
    invoiceBillingChange: 'Skift',
    invoiceBillingCancel: 'Annullér',
    invoiceBillingSaveCustom: 'Gem adresse',
    invoiceBillingCustomOption: 'Brugerdefineret adresse',
    invoiceBillingEmpty: 'Ingen faktureringsadresse valgt.',
    invoiceBillingFieldAttention: 'Att / Kontaktperson',
    invoiceBillingFieldAddress1: 'Adresse',
    invoiceBillingFieldAddress2: 'Adresse linje 2',
    invoiceBillingFieldPostalCode: 'Postnummer',
    invoiceBillingFieldPostalRegion: 'By',
    invoiceBillingFieldCountry: 'Land',
    invoiceBillingFieldMobilePhone: 'Telefon',
    invoiceBillingFieldReference: 'Reference',
    invoiceBillingRememberOnDevice: 'Husk mine oplysninger på denne enhed',

    invoiceShippingHeading: 'Leveringsadresse',
    invoiceShippingFoldedHint: 'Valgt adresse',
    invoiceShippingChange: 'Skift',
    invoiceShippingCancel: 'Annullér',
    invoiceShippingSaveCustom: 'Gem adresse',
    invoiceShippingCustomOption: 'Brugerdefineret adresse',
    invoiceShippingEmpty: 'Ingen leveringsadresse valgt.',

    invoiceDeliveryLabel: 'Levering',
    invoiceDeliveryDescription: 'Vi leverer til ønsket adresse',
    invoiceDeliveryFreeLabel: 'Gratis fragt',
    invoicePickupLabel: 'Afhent i butik',
    invoicePickupDescription: 'Afhent i vores butik (fragten annulleres)',
    invoicePickupHeading: 'Vælg afhentningssted',

    vatNumberNoticeMissing:
      'Virksomheden har ikke registreret et momsnummer, derfor beregnes moms efter dit lands sats. Tilføj et gyldigt VAT-nummer i virksomhedsprofilen for at handle uden moms.',
    vatNumberNoticeInvalid:
      'Momsnummeret ser ikke gyldigt ud – det skal begynde med landekoden, f.eks. PL9191127821. Indtil det er rettet, beregnes moms efter dit lands sats.',
    vatNumberNoticeReverseChargeUnverifiable:
      'Virksomheden er sat til omvendt betalingspligt, men momsnummeret mangler eller er forkert. Kontrollér nummeret før du bestiller – ellers kan ordren ikke bogføres.'
  },
  de: {
    orderModeUpfrontLabel: 'Jetzt bezahlen',
    orderModeUpfrontDescription: 'Bezahlen Sie Ihre Bestellung direkt an der Kasse.',
    orderModeInvoiceLabel: 'Rechnung',
    orderModeInvoiceDescription: 'Wird nach der Bestellung an das Unternehmen fakturiert.',
    orderModeInvoiceDescriptionProforma: 'Pro-forma-Rechnung – Zahlung vor Lieferung erforderlich.',

    checkoutSummaryOrderModeHeading: 'Zahlungsmethode',
    checkoutSummarySummaryContinueToReview: 'Rechnung prüfen',

    invoiceReviewHeading: 'Rechnung prüfen',
    invoiceReviewBackToCart: 'Zurück zum Warenkorb',
    invoiceReviewOrderPrefix: 'Bestellung #',
    invoiceReviewOrderHelp:
      'Eine Rechnung wird nach der Registrierung der Bestellung an das Unternehmen gesendet.',
    invoiceReviewOrderHelpProforma:
      'Eine Pro-forma-Rechnung wird an das Unternehmen gesendet. Die Bestellung wird nach Zahlungseingang versandt.',
    invoiceReviewItemsHeading: 'Artikel',
    invoiceReviewEmptyItems: 'Keine Artikel',
    invoiceReviewNetTotal: 'Zwischensumme exkl. MwSt.',
    invoiceReviewShipping: 'Versand',
    invoiceReviewShippingInclVat: 'inkl. MwSt.',
    invoiceReviewShippingExclVat: 'zzgl. MwSt.',
    invoiceReviewVat: 'MwSt.',
    invoiceReviewVatNoteReverseCharge: 'VAT 0 % innergemeinschaftliche Lieferung',
    invoiceReviewVatNoteExport: 'VAT 0 % Ausfuhr außerhalb der EU',
    invoiceReviewDiscount: 'Rabatt',
    invoiceReviewQuantityDiscount: 'Mengenrabatt',
    invoiceReviewTotal: 'Zu fakturierender Gesamtbetrag',
    invoiceReviewTotalProforma: 'Im Voraus zu zahlender Gesamtbetrag',
    invoiceReviewSubmitLabel: 'Bestellung absenden',
    invoiceReviewLoadError: 'Bestellung konnte nicht geladen werden. Bitte erneut versuchen.',

    invoiceConfirmationTitle: 'Vielen Dank für Ihre Bestellung!',
    invoiceConfirmationOrderNumberPrefix: 'Bestellnummer: ',
    invoiceConfirmationBody:
      'Eine Rechnung wird gemäß den vereinbarten Bedingungen an das Unternehmen gesendet. Sie können den Bestellstatus unter Meine Bestellungen verfolgen.',
    invoiceConfirmationBodyProforma:
      'Eine Pro-forma-Rechnung wird an das Unternehmen gesendet. Die Bestellung wird nach Zahlungseingang bearbeitet. Sie können den Bestellstatus unter Meine Bestellungen verfolgen.',

    invoiceBillingHeading: 'Rechnungsadresse',
    invoiceBillingFoldedHint: 'Ausgewählte Adresse',
    invoiceBillingChange: 'Ändern',
    invoiceBillingCancel: 'Abbrechen',
    invoiceBillingSaveCustom: 'Adresse speichern',
    invoiceBillingCustomOption: 'Eigene Adresse',
    invoiceBillingEmpty: 'Keine Rechnungsadresse ausgewählt.',
    invoiceBillingFieldAttention: 'z.Hd. / Ansprechpartner',
    invoiceBillingFieldAddress1: 'Adresse',
    invoiceBillingFieldAddress2: 'Adresse Zeile 2',
    invoiceBillingFieldPostalCode: 'Postleitzahl',
    invoiceBillingFieldPostalRegion: 'Ort',
    invoiceBillingFieldCountry: 'Land',
    invoiceBillingFieldMobilePhone: 'Telefon',
    invoiceBillingFieldReference: 'Referenz',
    invoiceBillingRememberOnDevice: 'Meine Daten auf diesem Gerät merken',

    invoiceShippingHeading: 'Lieferadresse',
    invoiceShippingFoldedHint: 'Ausgewählte Adresse',
    invoiceShippingChange: 'Ändern',
    invoiceShippingCancel: 'Abbrechen',
    invoiceShippingSaveCustom: 'Adresse speichern',
    invoiceShippingCustomOption: 'Eigene Adresse',
    invoiceShippingEmpty: 'Keine Lieferadresse ausgewählt.',

    invoiceDeliveryLabel: 'Lieferung',
    invoiceDeliveryDescription: 'Wir liefern an die gewünschte Adresse',
    invoiceDeliveryFreeLabel: 'Kostenloser Versand',
    invoicePickupLabel: 'Abholung im Geschäft',
    invoicePickupDescription: 'Sie holen in unserem Geschäft ab (Versand entfällt)',
    invoicePickupHeading: 'Abholort wählen',

    vatNumberNoticeMissing:
      'Für dieses Unternehmen ist keine Umsatzsteuer-Identifikationsnummer hinterlegt, daher wird die MwSt. nach dem Satz Ihres Landes berechnet. Hinterlegen Sie eine gültige USt-IdNr. im Firmenprofil, um ohne MwSt. einzukaufen.',
    vatNumberNoticeInvalid:
      'Die Umsatzsteuer-Identifikationsnummer scheint ungültig zu sein – sie muss mit dem Ländercode beginnen, z. B. PL9191127821. Bis zur Korrektur wird die MwSt. nach dem Satz Ihres Landes berechnet.',
    vatNumberNoticeReverseChargeUnverifiable:
      'Für dieses Unternehmen ist die innergemeinschaftliche Lieferung eingestellt, die USt-IdNr. fehlt jedoch oder ist fehlerhaft. Bitte prüfen Sie die Nummer vor der Bestellung – andernfalls kann der Auftrag nicht verbucht werden.'
  },
  fi: {
    orderModeUpfrontLabel: 'Maksa heti',
    orderModeUpfrontDescription: 'Maksa tilauksesi heti kassalla.',
    orderModeInvoiceLabel: 'Lasku',
    orderModeInvoiceDescription: 'Laskutetaan yritykseltä tilauksen jälkeen.',
    orderModeInvoiceDescriptionProforma: 'Proformalasku – maksu vaaditaan ennen toimitusta.',

    checkoutSummaryOrderModeHeading: 'Maksutapa',
    checkoutSummarySummaryContinueToReview: 'Tarkista lasku',

    invoiceReviewHeading: 'Tarkista lasku',
    invoiceReviewBackToCart: 'Takaisin ostoskoriin',
    invoiceReviewOrderPrefix: 'Tilaus #',
    invoiceReviewOrderHelp: 'Lasku lähetetään yritykselle, kun tilaus on rekisteröity.',
    invoiceReviewOrderHelpProforma:
      'Yritykselle lähetetään proformalasku. Tilaus toimitetaan, kun maksu on vastaanotettu.',
    invoiceReviewItemsHeading: 'Tuotteet',
    invoiceReviewEmptyItems: 'Ei tuotteita',
    invoiceReviewNetTotal: 'Välisumma ilman alv.',
    invoiceReviewShipping: 'Toimitus',
    invoiceReviewShippingInclVat: 'sis. ALV',
    invoiceReviewShippingExclVat: 'ilman ALV',
    invoiceReviewVat: 'ALV',
    invoiceReviewVatNoteReverseCharge: 'VAT 0 % yhteisömyynti (käännetty verovelvollisuus)',
    invoiceReviewVatNoteExport: 'VAT 0 % vienti EU:n ulkopuolelle',
    invoiceReviewDiscount: 'Alennus',
    invoiceReviewQuantityDiscount: 'Määräalennus',
    invoiceReviewTotal: 'Laskutettava yhteensä',
    invoiceReviewTotalProforma: 'Ennakkoon maksettava yhteensä',
    invoiceReviewSubmitLabel: 'Lähetä tilaus',
    invoiceReviewLoadError: 'Tilausta ei voitu ladata. Yritä uudelleen.',

    invoiceConfirmationTitle: 'Kiitos tilauksestasi!',
    invoiceConfirmationOrderNumberPrefix: 'Tilausnumero: ',
    invoiceConfirmationBody:
      'Lasku lähetetään yritykselle sovittujen ehtojen mukaisesti. Voit seurata tilauksen tilaa Omat tilaukset -kohdasta.',
    invoiceConfirmationBodyProforma:
      'Yritykselle lähetetään proformalasku. Tilaus käsitellään, kun maksu on vastaanotettu. Voit seurata tilauksen tilaa Omat tilaukset -kohdasta.',

    invoiceBillingHeading: 'Laskutusosoite',
    invoiceBillingFoldedHint: 'Valittu osoite',
    invoiceBillingChange: 'Muuta',
    invoiceBillingCancel: 'Peruuta',
    invoiceBillingSaveCustom: 'Tallenna osoite',
    invoiceBillingCustomOption: 'Mukautettu osoite',
    invoiceBillingEmpty: 'Laskutusosoitetta ei ole valittu.',
    invoiceBillingFieldAttention: 'Vast.henk. / Yhteyshenkilö',
    invoiceBillingFieldAddress1: 'Osoite',
    invoiceBillingFieldAddress2: 'Osoiterivi 2',
    invoiceBillingFieldPostalCode: 'Postinumero',
    invoiceBillingFieldPostalRegion: 'Kaupunki',
    invoiceBillingFieldCountry: 'Maa',
    invoiceBillingFieldMobilePhone: 'Puhelin',
    invoiceBillingFieldReference: 'Viite',
    invoiceBillingRememberOnDevice: 'Muista tietoni tällä laitteella',

    invoiceShippingHeading: 'Toimitusosoite',
    invoiceShippingFoldedHint: 'Valittu osoite',
    invoiceShippingChange: 'Muuta',
    invoiceShippingCancel: 'Peruuta',
    invoiceShippingSaveCustom: 'Tallenna osoite',
    invoiceShippingCustomOption: 'Mukautettu osoite',
    invoiceShippingEmpty: 'Toimitusosoitetta ei ole valittu.',

    invoiceDeliveryLabel: 'Toimitus',
    invoiceDeliveryDescription: 'Toimitamme haluamaasi osoitteeseen',
    invoiceDeliveryFreeLabel: 'Ilmainen toimitus',
    invoicePickupLabel: 'Nouto myymälästä',
    invoicePickupDescription: 'Nouto myymälästämme (toimitus peruutetaan)',
    invoicePickupHeading: 'Valitse noutopiste',

    vatNumberNoticeMissing:
      'Yritykselle ei ole tallennettu ALV-numeroa, joten arvonlisävero peritään oman maasi verokannan mukaan. Lisää voimassa oleva VAT-numero yritysprofiiliin ostaaksesi ilman arvonlisäveroa.',
    vatNumberNoticeInvalid:
      'ALV-numero ei näytä kelvolliselta – sen tulee alkaa maakoodilla, esim. PL9191127821. Kunnes se on korjattu, arvonlisävero peritään oman maasi verokannan mukaan.',
    vatNumberNoticeReverseChargeUnverifiable:
      'Yritykselle on asetettu käännetty verovelvollisuus, mutta ALV-numero puuttuu tai on virheellinen. Tarkista numero ennen tilaamista – muuten tilausta ei voida kirjata.'
  }
}

/**
 * Read the active locale from `<html lang>` and pick the matching translation
 * bundle. Falls back to Swedish — same default as the carrier widgets.
 */
export const getInvoicePaymentTranslations = (): InvoicePaymentOverrides => {
  if (typeof document === 'undefined') return invoicePaymentTranslations.sv
  const lang = (document.documentElement.lang || 'sv') as Language
  return invoicePaymentTranslations[lang] ?? invoicePaymentTranslations.sv
}
